import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from "rxjs";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {HttpClient} from "@angular/common/http";
import {FilterService} from "./filter.service";
import {DOCUMENT} from "@angular/common";
import {first, take} from "rxjs/operators";
import {Uuid} from "../interface/response/uuid";
import {CompetitionImport} from "../interface/competition-import";
import {CompetitionImportRow} from "../interface/competition-import-row";
import {ListResponse} from "../interface/response/list-response";
import {Document as DocumentInterface} from "../interface/document";

@Injectable({
  providedIn: 'root'
})
export class CompetitionImportService {
  public readonly importListSubject: ReplaySubject<CountedAndPaginated<CompetitionImport>>
    = new ReplaySubject<CountedAndPaginated<CompetitionImport>>(1);

  public readonly importRowsListSubject: ReplaySubject<CountedAndPaginated<CompetitionImportRow>>
    = new ReplaySubject<CountedAndPaginated<CompetitionImportRow>>(1);


  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public loadImportList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/competition-import?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<CompetitionImport>) => {
        this.importListSubject.next(result);
      });
  }

  public import(formData: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/competition-import/method/import`, formData)
      .pipe(
        take(1)
      ) as Observable<Uuid>;
  }

  public getImport(importId: string): Observable<CompetitionImport> {
    return this.httpClient.get(`/rest/crm/competition-import/${importId}`)
        .pipe(first()) as Observable<CompetitionImport>;
  }

  public deleteImport(uuid: string): Observable<Uuid>  {
    return this.httpClient.delete(`/rest/crm/competition-import/${uuid}`)
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadImportFile(competitionImportId: string): void {
    this.document.location.href = `/rest/crm/competition-import/${competitionImportId}/downloadFile`;
  }

  public createFromImport(formData: object): Observable<Uuid> {
    return this.httpClient.post<Uuid>('/rest/crm/competition-import', formData)
        .pipe(take(1));
  }

  public getCompetitionImportRows(competitionImportId: string, page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/competition-import/${competitionImportId}/import-rows?page=${page + 1}&pageSize=${pageSize}&`
      + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<CompetitionImportRow>) => {
        this.importRowsListSubject.next(result);
      });
  }

  public getCompetitionImportRow(uuid: string): Observable<CompetitionImportRow> {
    return this.httpClient.get(`/rest/crm/competition-import-row/${uuid}`)
      .pipe(first()) as Observable<CompetitionImportRow>;
  }

  public loadImportRowsList(page: number, pageSize: number, filters: object, sort?: { sortBy?: string; sortOrder?: string }): void {
    const sortBy = sort?.sortBy || 'createdAt';
    const sortOrder = sort?.sortOrder || 'ASC';

    this.httpClient.get(`/rest/crm/competition-import-rows?page=${page + 1}&pageSize=${pageSize}&sortBy=${sortBy}&sortOrder=${sortOrder}&`
      + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<CompetitionImportRow>) => {
        this.importRowsListSubject.next(result);
      });
  }

  public editImportRow(formData: object, importRowId: string): Observable<any> {
      return this.httpClient.put(`/rest/crm/competition-import-row/${importRowId}`, formData)
        .pipe(take(1));
  }

  public downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/competition-import-row/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public getCompetitionImportRowDocuments(uuid: string): Observable<ListResponse<DocumentInterface>> {
    return this.httpClient.get(`/rest/crm/competitionImportRow/${uuid}/document`)
      .pipe(first()) as Observable<ListResponse<DocumentInterface>>;
  }

}
